"use client";

import { useMemo, useState } from "react";
import { Sparkles, Search } from "lucide-react";
import { useStore } from "@/lib/store";
import { AgentRunCard } from "./agent-runs/AgentRunCard";
import { AgentHelpButton } from "./ui/AgentHelpButton";

/**
 * Workspace-wide list of every agent run, grouped by session, newest first.
 * Filters: by session, by agent identity, free-text search across prompt+summary.
 */
export function AgentRunsView() {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allRuns = useStore((s) => s.agentRuns);
  const allSessions = useStore((s) => s.agentSessions);
  const users = useStore((s) => s.users);

  const [query, setQuery] = useState("");
  const [sessionFilter, setSessionFilter] = useState<string>("");
  const [agentFilter, setAgentFilter] = useState<string>("");

  const wsSessions = useMemo(
    () =>
      allSessions
        .filter((s) => s.workspaceId === activeWorkspaceId)
        .sort((a, b) => b.startedAt - a.startedAt),
    [allSessions, activeWorkspaceId]
  );

  const wsBots = useMemo(() => {
    const botIds = new Set(
      allRuns
        .filter((r) => r.workspaceId === activeWorkspaceId)
        .map((r) => r.actorId)
        .filter((x): x is string => !!x)
    );
    return users.filter((u) => botIds.has(u.id));
  }, [allRuns, activeWorkspaceId, users]);

  const filtered = useMemo(() => {
    const ql = query.trim().toLowerCase();
    return allRuns
      .filter((r) => r.workspaceId === activeWorkspaceId)
      .filter((r) => !sessionFilter || r.sessionId === sessionFilter)
      .filter((r) => !agentFilter || r.actorId === agentFilter)
      .filter(
        (r) =>
          !ql ||
          r.prompt.toLowerCase().includes(ql) ||
          r.summary.toLowerCase().includes(ql) ||
          r.actions.some((a) => a.toLowerCase().includes(ql))
      );
  }, [allRuns, activeWorkspaceId, sessionFilter, agentFilter, query]);

  // Group by session
  const grouped = useMemo(() => {
    const bySession = new Map<string, typeof filtered>();
    for (const r of filtered) {
      const arr = bySession.get(r.sessionId) ?? [];
      arr.push(r);
      bySession.set(r.sessionId, arr);
    }
    return [...bySession.entries()].sort((a, b) => {
      const aMax = Math.max(...a[1].map((r) => r.createdAt));
      const bMax = Math.max(...b[1].map((r) => r.createdAt));
      return bMax - aMax;
    });
  }, [filtered]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 py-3 border-b border-[var(--border)] flex items-center gap-3">
        <Sparkles size={16} className="text-[var(--accent)]" />
        <h2 className="text-[14px] font-semibold">Agent runs</h2>
        <span className="text-[11.5px] text-[var(--fg-tertiary)]">·</span>
        <span className="text-[12px] text-[var(--fg-secondary)]">
          {filtered.length} run{filtered.length === 1 ? "" : "s"} across{" "}
          {grouped.length} session{grouped.length === 1 ? "" : "s"}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search
              size={11}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search prompts, summaries…"
              className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md pl-7 pr-2 py-1 text-[11.5px] outline-none focus:border-[var(--accent)] w-[220px]"
            />
          </div>
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-2 py-1 text-[11.5px] outline-none focus:border-[var(--accent)]"
          >
            <option value="">All agents</option>
            {wsBots.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
            className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-2 py-1 text-[11.5px] outline-none focus:border-[var(--accent)] max-w-[260px]"
          >
            <option value="">All sessions</option>
            {wsSessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <AgentHelpButton kind="agents" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 text-[var(--fg-muted)]">
            <Sparkles size={32} className="mb-3 opacity-40" />
            <div className="text-[13px] font-medium mb-1">No agent runs yet</div>
            <div className="text-[11.5px] max-w-md">
              Agents on this workspace record their work via{" "}
              <code>record_run</code>. Once the racing-tracker-dev (or any other
              agent) finishes a substantive task, the run will appear here grouped
              by session name.
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 space-y-6">
            {grouped.map(([sessionId, runs]) => {
              const session = wsSessions.find((s) => s.id === sessionId);
              return (
                <div key={sessionId}>
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[var(--bg)] py-1 z-10">
                    <Sparkles size={12} className="text-[var(--accent)]" />
                    <h3 className="text-[12.5px] font-semibold text-[var(--fg)]">
                      {session?.name ?? "Session not loaded"}
                    </h3>
                    <span className="text-[11px] text-[var(--fg-muted)]">
                      {runs.length} run{runs.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {runs.map((r) => (
                      <AgentRunCard key={r.id} run={r} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
